import { useEffect } from 'react'
import { FinderItem } from '../types'

interface UseFinderKeyboardProps {
    items: FinderItem[]
    selectedIds: Set<string>
    setSelectedIds: (ids: Set<string>) => void
    onOpen: (item: FinderItem) => void
    onDelete: (items: FinderItem[]) => void
    onRename: (item: FinderItem) => void
    onQuickLook: (item: FinderItem) => void
    enabled?: boolean
}

export function useFinderKeyboard({
    items,
    selectedIds,
    setSelectedIds,
    onOpen,
    onDelete,
    onRename,
    onQuickLook,
    enabled = true,
}: UseFinderKeyboardProps) {
    useEffect(() => {
        if (!enabled) return

        const handleKeyDown = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
                return
            }

            const selectedArray = items.filter(item => selectedIds.has(item.id))
            const lastSelectedId = Array.from(selectedIds).pop()
            const lastSelectedIndex = items.findIndex(i => i.id === lastSelectedId)

            switch (e.key) {
                case 'ArrowDown':
                case 'ArrowRight':
                    e.preventDefault()
                    if (items.length > 0) {
                        const nextIndex = lastSelectedIndex < items.length - 1 ? lastSelectedIndex + 1 : 0
                        const nextItem = items[nextIndex]
                        if (e.shiftKey) {
                            const newSet = new Set(selectedIds)
                            newSet.add(nextItem.id)
                            setSelectedIds(newSet)
                        } else {
                            setSelectedIds(new Set([nextItem.id]))
                        }
                    }
                    break

                case 'ArrowUp':
                case 'ArrowLeft':
                    e.preventDefault()
                    if (items.length > 0) {
                        const prevIndex = lastSelectedIndex > 0 ? lastSelectedIndex - 1 : items.length - 1
                        const prevItem = items[prevIndex]
                        if (e.shiftKey) {
                            const newSet = new Set(selectedIds)
                            newSet.add(prevItem.id)
                            setSelectedIds(newSet)
                        } else {
                            setSelectedIds(new Set([prevItem.id]))
                        }
                    }
                    break

                case 'Enter':
                    e.preventDefault()
                    if (selectedArray.length === 1) {
                        onOpen(selectedArray[0])
                    }
                    break

                case 'Delete':
                case 'Backspace':
                    if (e.metaKey || e.ctrlKey) {
                        e.preventDefault()
                        if (selectedArray.length > 0) {
                            onDelete(selectedArray)
                        }
                    }
                    break

                case 'F2':
                    e.preventDefault()
                    if (selectedArray.length === 1) {
                        onRename(selectedArray[0])
                    }
                    break

                case ' ':
                    e.preventDefault()
                    if (selectedArray.length === 1) {
                        onQuickLook(selectedArray[0])
                    }
                    break

                case 'a':
                    if (e.metaKey || e.ctrlKey) {
                        e.preventDefault()
                        setSelectedIds(new Set(items.map(i => i.id)))
                    }
                    break

                case 'Escape':
                    e.preventDefault()
                    setSelectedIds(new Set())
                    break
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [enabled, items, selectedIds, setSelectedIds, onOpen, onDelete, onRename, onQuickLook])
}
